import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { useWorkshopSchedule } from "../hooks/useWorkshopSchedule";

// Special offer countdown - 40 minutes from page load
const OFFER_DURATION = 40 * 60 * 1000; // 40 minutes in milliseconds

function useOfferCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedEndTime = localStorage.getItem("offerEndTime");
    if (savedEndTime) {
      const endTime = parseInt(savedEndTime);
      const remaining = Math.max(0, endTime - Date.now());
      if (remaining > 0) {
        return getOfferTimeParts(remaining);
      }
    }
    // Start new countdown
    const endTime = Date.now() + OFFER_DURATION;
    localStorage.setItem("offerEndTime", endTime.toString());
    return getOfferTimeParts(OFFER_DURATION);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const savedEndTime = localStorage.getItem("offerEndTime");
      if (savedEndTime) {
        const endTime = parseInt(savedEndTime);
        const remaining = Math.max(0, endTime - Date.now());
        if (remaining === 0) {
          // Restart countdown
          const newEndTime = Date.now() + OFFER_DURATION;
          localStorage.setItem("offerEndTime", newEndTime.toString());
          setTimeLeft(getOfferTimeParts(OFFER_DURATION));
        } else {
          setTimeLeft(getOfferTimeParts(remaining));
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

function getOfferTimeParts(milliseconds) {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  return { days, hours, minutes, seconds };
}

export default function HeroSection({ openRegistration = () => {} }) {
  const { workshopDetails } = useWorkshopSchedule();
  const offerTimeLeft = useOfferCountdown();

  const handleRegisterClick = () => {
    openRegistration();
  };

  return (
    <header className="hero-container">
      {/* Top thin bar */}
      <div className="hero-top-bar">
        🚀 10x AI Workshop - Automate Your Business & 10x Your Revenue
      </div>

      {/* Hero area */}
      <section className="hero-section">
        <div className="hero-inner">
          {/* Headline Area - Centered above columns */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="hero-headline-area"
          >
            {/* Big headline */}
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              "24/7 Business-ல வேலை செஞ்சு Burnout ஆகிடுச்சா?"
              <br />
              <span className="highlight">
                Growth-ல focus பண்ண முடியாம, சின்ன சின்ன வேலை-ல time waste
                பண்றீங்களா?.
              </span>
              <br />
              <span className="subtitle-text">
                உங்க 90% Admin Task-ஐ (Bookings, Reminders) AI Agent-கிட்ட
                குடுங்க.
              </span>
            </motion.h1>

            {/* Body text */}
            <motion.p
              style={{
                fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
                color: "#F2C94C",
                marginBottom: "2rem",
                lineHeight: "1.6",
                textAlign: "center",
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Learn the Step-by-Step Secrets to Autopilot Your Business.
            </motion.p>
          </motion.div>

          {/* Two-column layout */}
          <div className="hero-columns">
            {/* --- CHANGED SECTION --- */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hero-left-column"
            >
              <motion.div
                className="hero-image-card" // Re-using this class for consistent shadow/border
                whileHover={{ scale: 1.02 }} // Kept the hover effect
                style={{
                  overflow: "hidden", // Ensures video corners match card's rounded corners
                  aspectRatio: "1 / 1", // <-- THIS IS THE KEY CHANGE (from 520/400)
                  backgroundColor: "#000", // Background for poster load-in
                }}
              >
                <video
                  src="https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/695573d49e7c133bba442bd0.mp4"
                  poster="https://storage.files-vault.com/uploads/1760945103-H0PweDnHcx.webp"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls // Adds default play/pause/mute controls
                  style={{
                    display: "block",
                    width: "100%", // Fill the container
                    height: "100%", // Fill the container
                    objectFit: "cover", // Cover the 1:1 area, maintaining aspect ratio
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </motion.div>
            </motion.div>
            {/* --- END CHANGED SECTION --- */}

            {/* RIGHT: Offer Panel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hero-right-column"
            >
              <div className="offer-panel shine-animation">
                <motion.div
                  className="offer-header"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <FiCheck className="offer-check" />
                  <span>
                    Join the LIVE Session & Learn AI Automation
                    <br />
                    for Your Business! 🆓
                  </span>
                </motion.div>

                <div className="offer-expiry">
                  <div className="expiry-text">Special Offer Ends In...</div>
                  <motion.div
                    className="countdown-display"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.days}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.days || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Days</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.hours}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.hours || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Hours</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number"
                        key={offerTimeLeft.minutes}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.minutes || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Minutes</div>
                    </div>
                    <div className="countdown-unit">
                      <motion.div
                        className="countdown-number pulse-animation"
                        key={offerTimeLeft.seconds}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {String(offerTimeLeft.seconds || 0).padStart(2, "0")}
                      </motion.div>
                      <div className="countdown-label">Seconds</div>
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  className="offer-time"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1 }}
                >
                  Next Batch: {workshopDetails.dayOfWeek}, {workshopDetails.formattedDate} at {workshopDetails.timeString}
                </motion.div>

                <motion.button
                  onClick={handleRegisterClick}
                  className="offer-cta"
                  aria-haspopup="dialog"
                  aria-label="Workshop-க்கு Register பண்ணுங்க"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  WORKSHOP-க்கு REGISTER பண்ணுங்க
                </motion.button>

                <motion.div
                  className="offer-bonus"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 1.4 }}
                >
                  <div className="bonus-text">
                    <span className="flame-icon">🔥</span>
                    Limited Time - Only 50 Seats Available!
                  </div>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: "0%" }}
                        animate={{ width: "65%" }}
                        transition={{ duration: 2, delay: 1.6 }}
                      ></motion.div>
                    </div>
                    <div className="progress-text">
                      65% Seats Already Booked • AI Automation Secrets + Bonus
                      Materials FREE
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </header>
  );
}